import { useDeviceSetting } from '@battleops/client/src/data/useDeviceSetting';
import React, { useId, useMemo, useState } from 'react';
import { STATUS_LABEL, GOAL_PERIOD_LABEL, goalTag, formatGoalValue, goalGapText, headlineGoals, deptNeon, scoreDelta, monthScores, scoreEmptyHint, SCORE_HERO as K } from '@battleops/shared';
import { TONE } from './DeptParts';
import Icon from '../Icon';
import s from './ScoreCard.module.css';

const ALL_NEON = ['#FF2E63', '#9D5CFF', '#00E1FF'];   // the phone's NEON._ — All's own colours

// The department's score hero on the web — the phone's ScoreHero (DeptUI.js), one story: the same measures
// (shared SCORE_HERO), copy and layout (Lj's review, Oct 11). Stacked, never split: the eyebrow (department · week,
// the on-pace / behind / avg chips, Week | Month), the gauge (0–100, a tick where "on pace" starts, the outer ring =
// goals on pace, the number, the word with its icon, the change on last week), the headline three (ring, value / target,
// a bullet bar with the pace mark, name, period), every other goal as a smaller tile, then the daily score as bars.
// No goals → a compact hero with one button. Glow only on the ring strokes.

const STATUS_ICON = { met: 'check-circle', ahead: 'trending-up', under: 'check-circle', on_pace: 'check', close: 'minus', behind: 'trending-down', over: 'alert-triangle', off: 'minus', none: 'minus', unset: 'target', feed: 'clock' };
const GOOD = ['met', 'ahead', 'on_pace', 'under'];
const DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/** A ring: dark track, gradient arc from 12 o'clock, a soft glow under the arc only; optional tick at `tick` (0–1). */
export function NeonRing({ size, stroke, pct, colors, tick = null, glow = true }) {
  const id = `nr${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const r = (size - stroke) / 2 - 6;
  const c = 2 * Math.PI * r;
  const on = c * Math.max(0, Math.min(1, pct || 0));
  const mid = size / 2;
  const rot = `rotate(-90 ${mid} ${mid})`;
  const arc = (w, op, cls) => <circle className={cls} cx={mid} cy={mid} r={r} stroke={`url(#${id})`} strokeOpacity={op} strokeWidth={w} fill="none" strokeLinecap="round" strokeDasharray={`${on} ${c}`} transform={rot} />;
  const a = tick == null ? null : (tick * 2 * Math.PI) - Math.PI / 2;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className={s.ringSvg} aria-hidden="true">
      <defs><linearGradient id={id} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={colors[0]} /><stop offset="0.5" stopColor={colors[1]} /><stop offset="1" stopColor={colors[2]} /></linearGradient></defs>
      <circle cx={mid} cy={mid} r={r} stroke="#2A221D" strokeWidth={stroke} fill="none" />
      {on > 0 ? <g>{glow ? arc(stroke + 10, 0.1, s.arc) : null}{glow ? arc(stroke + 4, 0.2, s.arc) : null}{arc(stroke, 1, s.arc)}</g> : null}
      {a != null ? <line x1={mid + Math.cos(a) * (r - stroke / 2 - 4)} y1={mid + Math.sin(a) * (r - stroke / 2 - 4)} x2={mid + Math.cos(a) * (r + stroke / 2 + 4)} y2={mid + Math.sin(a) * (r + stroke / 2 + 4)} stroke="#FBF6EE" strokeWidth={2} strokeLinecap="round" /> : null}
    </svg>
  );
}

/** A goal's bullet bar: the value against the target, the white mark = where it should be by now. */
function Bullet({ g, color }) {
  const max = g.target ? Math.max(g.target, g.value || 0) * (g.direction === 'down' ? 1.2 : 1) : 1;
  const fill = g.value != null && g.target != null ? Math.max(0, Math.min(100, (g.value / max) * 100)) : 0;
  const mark = g.target == null ? null : g.kind === 'count' && g.direction === 'up' && g.expected != null ? (g.expected / max) * 100 : (g.target / max) * 100;
  return (
    <span className={s.bullet} aria-hidden="true">
      <span style={{ width: `${fill}%`, background: g.status === 'over' ? TONE.over : color }} />
      {mark != null ? <i style={{ left: `${Math.min(98.5, mark)}%` }} /> : null}
    </span>
  );
}

/** The daily score as bars: today full strength, the average dashed, the day under each; ghost bars when empty. */
function ScoreBars({ points, colors, month }) {
  const scored = points.filter((p) => p.score != null);
  const avg = scored.length ? Math.round(scored.reduce((a, p) => a + p.score, 0) / scored.length) : null;
  const slots = month ? points : Array.from({ length: 7 }, (_, i) => points[i] || { date: null, score: null, future: true });
  const n = slots.length; const W = 700; const Hh = 120; const gap = month ? 4 : 14; const bw = (W - gap * (n - 1)) / n;
  const y = (v) => Hh - (Math.max(0, Math.min(100, v)) / 100) * (Hh - 6);
  const lastIdx = points.length - 1;
  return (
    <div className={s.bars}>
      <svg viewBox={`0 0 ${W} ${Hh}`} preserveAspectRatio="none" className={s.barSvg} role="img" aria-label={`Score each day: ${points.map((p) => `${p.date} ${p.score ?? 'none'}`).join(', ')}${avg != null ? `; average ${avg}` : ''}`}>
        {[50, 100].map((v) => <line key={v} x1={0} x2={W} y1={y(v)} y2={y(v)} stroke="#2A221D" strokeWidth={1} vectorEffect="non-scaling-stroke" />)}
        {slots.map((p, i) => {
          const x = i * (bw + gap);
          if (p.score == null) return <rect key={i} x={x} y={Hh - 6} width={bw} height={6} rx={3} fill="#2A221D" />;
          const today = i === lastIdx;
          return (
            <rect key={i} x={x} y={y(p.score)} width={bw} height={Hh - y(p.score)} rx={4} fill={today ? colors[1] : colors[0]} fillOpacity={today ? 1 : 0.55}>
              <title>{`${new Date(`${p.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}: ${p.score}`}</title>
            </rect>
          );
        })}
        {avg != null ? <line x1={0} x2={W} y1={y(avg)} y2={y(avg)} stroke="#FBF6EE" strokeOpacity={0.55} strokeWidth={1.5} strokeDasharray="6 6" vectorEffect="non-scaling-stroke" /> : null}
      </svg>
      <div className={s.barDays} style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
        {slots.map((p, i) => <span key={i} data-today={i === lastIdx || undefined}>{month ? (i % 5 === 0 || i === lastIdx ? new Date(`${p.date}T12:00:00`).getDate() : '') : DAYS[i]}</span>)}
      </div>
    </div>
  );
}

/**
 * The score hero. onOpen(goalId?) = the breakdown (a goal → that goal); no goals → onOpen() builds the first.
 * `data` (useDeptGoals' data) adds the change on last week and the Month view.
 */
export function ScoreHero({ dept, view, week = [], data = null, subtitle, onOpen, pageRange = false }) {
  // ONE range per department Home, remembered on this device; every card below reads the same key
  const [range, setRange] = useDeviceSetting(`home.range.${dept?.id || 'all'}`, 'week', ['week', 'month']);
  const status = view?.status || null;
  const goals = view?.goals || [];
  const neon = deptNeon(dept);
  const marked = goals.filter((g) => g.mark != null);
  const good = marked.filter((g) => GOOD.includes(g.status)).length;
  const behind = marked.length - good;
  const scored = week.filter((p) => p.score != null);
  const avg = scored.length ? Math.round(scored.reduce((a, p) => a + p.score, 0) / scored.length) : null;
  const delta = useMemo(() => (data && goals.length ? scoreDelta(dept, data) : null), [dept, data, goals.length]);
  const month = useMemo(() => (data && range === 'month' ? monthScores(dept, data) : null), [dept, data, range]);
  const { top, rest } = headlineGoals(goals);
  const score = view?.score ?? null;
  const summary = `${dept.name} score ${score ?? 'none'} of 100${status ? `, ${STATUS_LABEL[status].toLowerCase()}` : ''}${marked.length ? `, ${good} on pace, ${behind} behind` : ''}`;
  const tileLabel = (g) => `${g.label}: ${formatGoalValue(g.value, g.unit)}${g.target != null ? ` of ${formatGoalValue(g.target, g.unit)}` : ''}, ${STATUS_LABEL[g.status]}`;
  // a target that follows your BattleBooks rate says so (Lj, Oct 11: "Earned per hour read 'of $30/h'" — it was the rate link)
  const ofText = (g) => (g.target != null ? `${g.direction === 'down' ? 'limit' : 'of'} ${formatGoalValue(g.target, g.unit)}` : g.needsRate ? 'set your rate' : 'no target');
  const rateNote = (g) => (g.targetSource === 'rate' ? 'from your BattleBooks rate' : g.targetSource === 'fallback' ? 'until your BattleBooks rate is set' : g.targetSource === 'jobs' ? 'from your jobs’ bids and rates' : g.targetSource === 'jobsFallback' ? 'until a job has a bid or rate' : null);
  const style = { '--n0': neon[0], '--n1': neon[1], '--n2': neon[2], '--ring': `${K.ring}px`, '--ringN': `${K.ringNarrow}px`, '--r': `${K.radius}px`, '--tr': `${K.tileRadius}px` };

  // No goals: the same centred stack, shorter — eyebrow, a smaller ring with "—", one line, one button, one muted line.
  // Never a row: nothing sits beside the ring at any width (Lj, Oct 11).
  if (!goals.length) {
    return (
      <section className={`${s.hero} ${s.empty}`} style={style} aria-label={summary}>
        <div className={s.eyebrow}><span className={s.kick}><span className={s.dot} />{String(dept.name).toUpperCase()}{subtitle ? ` · ${String(subtitle).toUpperCase()}` : ''}</span></div>
        <span className={s.dialSmall}><NeonRing size={K.ringEmpty} stroke={K.innerStroke} pct={0} colors={neon} /><span className={s.center}><span className={s.numSmall}>—</span></span></span>
        <span className={s.word}>NO SCORE YET</span>
        {view?.role !== 'personal' ? <button type="button" className={s.cta} onClick={() => onOpen?.()}><Icon name="plus" size={16} />{K.label.empty}</button> : null}
        <span className={s.emptyHint}>{scoreEmptyHint(dept)}</span>
      </section>
    );
  }

  return (
    <section className={s.hero} style={style} aria-label={summary}>
      <div className={s.eyebrow}>
        <span className={s.kick}><span className={s.dot} />{String(dept.name).toUpperCase()}{subtitle ? ` · ${String(subtitle).toUpperCase()}` : ''}</span>
        <span className={s.chips}>
          {marked.length ? <span className={s.chip}><Icon name="check" size={12} style={{ color: TONE.met }} />{good} {K.label.onPace}</span> : null}
          {behind ? <span className={s.chip}><Icon name="trending-down" size={12} style={{ color: TONE.behind }} />{behind} {K.label.behind}</span> : null}
          {avg != null ? <span className={s.chip}>{K.label.avg} {avg}</span> : null}
          {pageRange ? null : (
          <span className={s.switch} role="tablist" aria-label="Score range">
            {['week', 'month'].map((r) => <button key={r} type="button" role="tab" aria-selected={range === r} data-on={range === r || undefined} onClick={() => setRange(r)}>{r === 'week' ? 'Week' : 'Month'}</button>)}
          </span>
          )}
        </span>
      </div>

      <button type="button" className={s.dial} onClick={() => onOpen?.()} aria-label={`${summary}. Open the breakdown`}>
        <NeonRing size={K.ring} stroke={K.outerStroke} pct={marked.length ? good / marked.length : 0} colors={[neon[2], neon[1], neon[0]]} glow={false} />
        <span className={s.inner} style={{ inset: `${(K.innerInset / K.ring) * 100}%` }}><NeonRing size={K.ring - K.innerInset * 2} stroke={K.innerStroke} pct={(score ?? 0) / 100} colors={neon} tick={K.paceLine / 100} /></span>
        <span className={s.center}>
          <span className={s.bigNum}>{score ?? '—'}</span>
          {score != null ? <span className={s.outOf}>/100</span> : null}
        </span>
        <span className={s.word} style={status ? { color: TONE[status] } : null}>{status ? <Icon name={STATUS_ICON[status]} size={13} /> : null}{status ? STATUS_LABEL[status] : 'NO DATA'}</span>
      </button>
      {delta?.delta != null ? <span className={s.delta} style={{ color: delta.delta > 0 ? TONE.met : delta.delta < 0 ? TONE.behind : undefined }}>{delta.delta > 0 ? '▲' : delta.delta < 0 ? '▼' : '—'} {Math.abs(delta.delta)} on the same day last week</span> : null}

      <div className={s.week}>
        <div className={s.weekHead}><span>{range === 'month' ? 'THIS MONTH · SCORE EACH DAY' : 'THIS WEEK · SCORE EACH DAY'}</span>{avg != null && range === 'week' ? <b>avg {avg} <i className={s.dash} /></b> : null}</div>
        <ScoreBars points={range === 'month' ? month || [] : week} colors={neon} month={range === 'month'} />
        {!scored.length && range === 'week' ? <div className={s.weekEmpty}>The bars fill in as the days count.</div> : null}
      </div>
      {/* the goals as small dials under the week (Lj, Oct 11: "smaller radio dials, hover to enlarge … under the weekly
          score chart"): the headline three a little larger; hover / focus lifts a card with the target, the pace mark,
          the name and the period */}
      <div className={s.dials}>
        {[...top.map((g) => [g, true]), ...rest.map((g) => [g, false])].map(([g, head]) => (
          <button key={g.id} type="button" className={s.dialBtn} data-head={head || undefined} onClick={() => onOpen?.(g.id)} aria-label={`${tileLabel(g)}${rateNote(g) ? `, ${rateNote(g)}` : ''}. Open its breakdown`}>
            <span className={s.mini}>
              <NeonRing size={head ? K.headRing : K.tileRing + 18} stroke={head ? K.headStroke : K.tileStroke} pct={(g.mark ?? 0) / 100} colors={neon} glow={head} />
              <span className={s.miniNum}>{g.mark ?? '—'}</span>
            </span>
            <span className={s.miniVal}>{g.status === 'feed' ? '—' : formatGoalValue(g.value, g.unit)}</span>
            <span className={s.miniName}>{g.label}</span>
            <span className={s.peek} role="tooltip">
              <b>{g.label}</b>
              <span className={s.peekVal}>{g.status === 'feed' ? 'waiting on the feed' : formatGoalValue(g.value, g.unit)} <small>{ofText(g)}</small></span>
              {rateNote(g) ? <span className={s.rateNote} style={{ marginTop: 0 }}>{rateNote(g)}</span> : null}
              <Bullet g={g} color={neon[1]} />
              <span className={s.meta}><span className={s.period}>{g.period === 'until' ? goalTag(g) : `${(GOAL_PERIOD_LABEL[g.period] || g.period).toUpperCase()}${g.direction === 'down' ? ' · LIMIT' : ''}`}</span><span className={s.status} style={{ color: TONE[g.status] }}><Icon name={STATUS_ICON[g.status]} size={11} />{STATUS_LABEL[g.status]}</span></span>
              {goalGapText(g) ? <span className={s.peekGap}>{goalGapText(g)}</span> : null}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

/** The overall score each day as a neon line (the phone's NeonLine): gradient stroke over a faint area, a dot per day, today lit. */
function NeonLine({ points, colors }) {
  const id = `nl${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const slots = Array.from({ length: 7 }, (_, i) => points[i] || { date: null, score: null });
  const W = 700; const Hh = 110; const pad = 10;
  const x = (i) => ((i + 0.5) * W) / 7;   // the middle of each day's column, under its label
  const y = (v) => pad + (Hh - pad * 2) * (1 - Math.max(0, Math.min(100, v)) / 100);
  const pts = slots.map((p, i) => (p.score == null ? null : [x(i), y(p.score), p, i])).filter(Boolean);
  const path = pts.map(([px, py], k) => `${k ? 'L' : 'M'}${px.toFixed(1)},${py.toFixed(1)}`).join(' ');
  const area = pts.length > 1 ? `${path} L${pts[pts.length - 1][0].toFixed(1)},${Hh} L${pts[0][0].toFixed(1)},${Hh} Z` : null;
  const last = points.length - 1;
  return (
    <div className={s.bars}>
      <svg viewBox={`0 0 ${W} ${Hh}`} preserveAspectRatio="none" className={s.barSvg} role="img" aria-label={`Overall score each day: ${points.map((p) => `${p.date} ${p.score ?? 'none'}`).join(', ')}`}>
        <defs>
          <linearGradient id={`${id}s`} x1="0" x2="1" y1="0" y2="0"><stop offset="0" stopColor={colors[0]} /><stop offset="0.5" stopColor={colors[1]} /><stop offset="1" stopColor={colors[2]} /></linearGradient>
          <linearGradient id={`${id}a`} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={colors[1]} stopOpacity=".28" /><stop offset="1" stopColor={colors[1]} stopOpacity="0" /></linearGradient>
        </defs>
        {[50, 100].map((v) => <line key={v} x1={0} x2={W} y1={y(v)} y2={y(v)} stroke="#2A221D" strokeWidth={1} vectorEffect="non-scaling-stroke" />)}
        {area ? <path d={area} fill={`url(#${id}a)`} /> : null}
        {pts.length ? <line x1={0} x2={W} y1={y(Math.round(pts.reduce((a, q) => a + q[2].score, 0) / pts.length))} y2={y(Math.round(pts.reduce((a, q) => a + q[2].score, 0) / pts.length))} stroke="#FBF6EE" strokeOpacity={0.55} strokeWidth={1.5} strokeDasharray="6 6" vectorEffect="non-scaling-stroke" /> : null}
        {pts.length > 1 ? <path d={path} fill="none" stroke={`url(#${id}s)`} strokeWidth={3} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" /> : null}
      </svg>
      <div className={s.lineDots}>
        {slots.map((p, i) => (p.score == null ? <span key={i} /> : <span key={i} style={{ left: `${(x(i) / W) * 100}%`, top: `${(y(p.score) / Hh) * 100}%` }} data-today={i === last || undefined} title={`${p.date}: ${p.score}`} />))}
      </div>
      <div className={s.barDays} style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
        {slots.map((p, i) => <span key={i} data-today={i === last || undefined}>{DAYS[i]}</span>)}
      </div>
    </div>
  );
}

/**
 * "All" on the web (Lj, Oct 11: "the overall score and then all the other department scores under it, exactly the way
 * mobile is — that should be the hero"): the phone's AllHero (DeptUI.js), one centred stack from the same SCORE_HERO
 * measures — the overall gauge (outer ring = departments not behind, inner = the business average; Personal shows but
 * never counts), the on pace / behind / week avg chips, every department as a ring tile (click = that department's
 * Home), then the overall score each day this week. Nothing sits beside the ring.
 */
export function AllHero({ all, onPick, subtitle }) {
  const neon = ALL_NEON;
  const biz = all.departments.filter((r) => !r.personal);
  const scored = biz.filter((r) => r.view.score != null);
  const okCount = scored.filter((r) => r.view.status !== 'behind').length;
  const wk = all.week.filter((p) => p.score != null);
  const avgWeek = wk.length ? Math.round(wk.reduce((a, p) => a + p.score, 0) / wk.length) : null;
  const status = all.status || null;
  const summary = `Overall score ${all.overall ?? 'none'} of 100${status ? `, ${STATUS_LABEL[status].toLowerCase()}` : ''}, ${okCount} of ${scored.length} departments on pace`;
  const style = { '--n0': neon[0], '--n1': neon[1], '--n2': neon[2], '--ring': `${K.ring}px`, '--ringN': `${K.ringNarrow}px`, '--r': `${K.radius}px`, '--tr': `${K.tileRadius}px` };
  return (
    <section className={s.hero} style={style} aria-label={summary}>
      <div className={s.eyebrow} style={{ justifyContent: 'center' }}>
        <span className={s.kick}><span className={s.dot} style={{ background: neon[2] }} />ALL{subtitle ? ` · ${String(subtitle).toUpperCase()}` : ''}</span>
      </div>
      <div className={s.dial} style={{ cursor: 'default' }} role="img" aria-label={summary}>
        <NeonRing size={K.ring} stroke={K.outerStroke} pct={scored.length ? okCount / scored.length : 0} colors={[neon[2], neon[1], neon[0]]} glow={false} />
        <span className={s.inner} style={{ inset: `${(K.innerInset / K.ring) * 100}%` }}><NeonRing size={K.ring - K.innerInset * 2} stroke={K.innerStroke} pct={(all.overall ?? 0) / 100} colors={neon} tick={K.paceLine / 100} /></span>
        <span className={s.center}>
          <span className={s.bigNum}>{all.overall ?? '—'}</span>
          {all.overall != null ? <span className={s.outOf}>/100</span> : null}
        </span>
        <span className={s.word} style={status ? { color: TONE[status] } : null}>{status ? <Icon name={STATUS_ICON[status]} size={13} /> : null}{status ? STATUS_LABEL[status] : 'OVERALL'}</span>
      </div>
      {scored.length ? (
        <span className={s.chips} style={{ justifyContent: 'center', marginTop: 6 }}>
          <span className={s.chip}><Icon name="check" size={12} style={{ color: TONE.met }} />{okCount} {K.label.onPace}</span>
          {all.behind ? <span className={s.chip}><Icon name="trending-down" size={12} style={{ color: TONE.behind }} />{all.behind} {K.label.behind}</span> : null}
          {avgWeek != null ? <span className={s.chip}>week {K.label.avg} {avgWeek}</span> : null}
        </span>
      ) : null}
      <div className={s.depts}>
        {all.departments.map((r) => {
          const n = deptNeon(r.dept);
          const word = r.view.status ? STATUS_LABEL[r.view.status] : r.personal ? 'PRIVATE' : r.view.unset ? 'SET GOALS' : 'NO DATA';
          return (
            <button key={r.dept.id} type="button" className={s.tile} onClick={() => onPick?.(r.dept.id)}
              title={r.personal ? 'Private — shows here, never in the overall' : undefined} aria-label={`${r.dept.name}: ${r.view.score ?? 'no score'}${r.view.status ? `, ${STATUS_LABEL[r.view.status]}` : ''}${r.personal ? ', private, not in the overall' : ''}. Open its Home`}>
              <span className={s.tileRing}>
                <NeonRing size={K.tileRing} stroke={K.tileStroke} pct={(r.view.score ?? 0) / 100} colors={n} glow={false} />
                <span className={s.tileNum} style={r.view.score != null ? { color: n[1] } : null}>{r.view.score ?? '—'}</span>
              </span>
              <span className={s.tileText}>
                <span className={s.tileName}>{r.dept.icon ? `${r.dept.icon} ` : ''}{r.dept.name}</span>
                <span className={s.status} style={{ color: r.view.status ? TONE[r.view.status] : 'var(--c-text-tertiary)' }}>{r.view.status ? <Icon name={STATUS_ICON[r.view.status]} size={11} /> : null}{word}</span>
              </span>
            </button>
          );
        })}
      </div>
      {all.week.length ? (
        <div className={s.week}>
          <div className={s.weekHead}><span>THIS WEEK · OVERALL</span>{avgWeek != null ? <b>avg {avgWeek} <i className={s.dash} /></b> : null}</div>
          <NeonLine points={all.week} colors={neon} />
          {!wk.length ? <div className={s.weekEmpty}>The bars fill in as the days count.</div> : null}
        </div>
      ) : null}
    </section>
  );
}
